import { Component, inject, signal, computed, input, output, isDevMode } from '@angular/core';
import {
  AlertService,
  AlertType,
  ButtonLoadingDirective,
  CardComponent,
  CardContentComponent,
  IconComponent,
} from '@gains-com/ui-angular';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RedemptionDialogHeaderComponent } from '../redemption-dialog-header/redemption-dialog-header.component.js';
import { MatButton } from '@angular/material/button';
import { MINIMUM_REDEMPTION_AMOUNT } from '../../common.js';
import {
  AnalyticsEvent,
  CurrencyId,
  decimal,
  generateId,
  PaymentMethodEntityTypeId,
  PaymentProviderAccountApprovalStatus,
} from '@gains-com/shared';
import { PaymentMethodAndUserBlockchainAddress, PaymentMethodsService } from '../../../../core/payment-methods.js';
import { RedemptionsService } from '../../../../core/redemption.service.js';
import { catchError, combineLatest, EMPTY, exhaustMap, filter, finalize, from, switchMap, tap, timer } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CurrencyPipe, formatDate } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '../../../../../environments/environment.js';
import { HttpClient, HttpErrorResponse, httpResource } from '@angular/common/http';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider';
import { Analytics } from '../../../../core/analytics/analytics.service.js';
import { ActiveRedemptionViewService } from '../../../../core/view-states/active-redemption-view.service.js';
import { CurrencyUIConfiguration, CurrencyVM } from '../../../../core/payments-common.js';
import { CurrenciesViewService } from '../../../../core/view-states/currencies-view.service.js';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { PaymentMethodsViewService } from '../../../../core/view-states/payment-methods-view.service.js';
import { AccountLimitsViewService } from '../../../../core/view-states/account-limits-view.service.js';
import { PreciseDate } from '@google-cloud/precise-date';
import { DecimalPipe } from '../../../../pipes/decimal.pipe.js';

interface CryptoForm {
  ticker: FormControl<string | null>;
  currency: FormControl<string | null>;
  address: FormControl<string | null>;
}

// How long to wait for Fireblocks approval (via realtime PM push) before
// resetting the submitting state. Matches the native _cryptoApprovalTimeout.
const SUBMITTING_TIMEOUT_MS = 30_000;

function getMajorityColorFromBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new window.Image();
    img.crossOrigin = 'Anonymous'; // In case you need CORS

    img.onload = function () {
      // Create canvas and draw image
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);

      // Get pixel data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Count colors
      const colorCount: Record<string, number> = {};
      let maxCount = 0;
      let majorityColor = '';

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const color = `${r},${g},${b}`;
        colorCount[color] = (colorCount[color] || 0) + 1;

        if (colorCount[color] > maxCount) {
          maxCount = colorCount[color];
          majorityColor = color;
        }
      }

      URL.revokeObjectURL(url); // Clean up
      resolve(majorityColor);
    };

    img.onerror = function (e) {
      URL.revokeObjectURL(url);
      reject(e);
    };

    img.src = url;
  });
}

interface TickerVM extends CurrencyUIConfiguration {
  ticker: string;
  display_name: string;
  is_multi_network: boolean;
  currencies: CurrencyVM[];
}

@Component({
  selector: 'app-crypto-redemption',
  imports: [
    CardComponent,
    CardContentComponent,
    MatOption,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatSelectModule,
    MatInput,
    RedemptionDialogHeaderComponent,
    MatButton,
    ButtonLoadingDirective,
    IconComponent,
    MatProgressSpinner,
    CurrencyPipe,
    DecimalPipe,
  ],
  templateUrl: './crypto-redemption.component.html',
  styleUrls: ['../styles.scss', './crypto-redemption.component.scss'],
})
export class CryptoRedemptionComponent {
  // Service Injections
  private readonly _redemptionsApi = inject(RedemptionsService);
  private readonly _paymentMethods = inject(PaymentMethodsService);
  private readonly _paymentMethodsViewService = inject(PaymentMethodsViewService);
  private readonly _http = inject(HttpClient);
  private readonly _analytics = inject(Analytics);
  private readonly _activeRedemptionViewService = inject(ActiveRedemptionViewService);
  private readonly _alertService = inject(AlertService);
  private readonly _currencies = inject(CurrenciesViewService);
  private readonly _userView = inject(UserViewService);
  private readonly _accountLimitsView = inject(AccountLimitsViewService);
  private readonly _logger = inject(LOGGER_FACTORY).create('CryptoRedemption');

  // Input/Output Properties
  amount = input.required<number>();
  back = output<void>();
  close = output<void>();

  // Form Controls
  cryptoForm = new FormGroup<CryptoForm>({
    ticker: new FormControl<string | null>(null),
    currency: new FormControl<string | null>(null),
    address: new FormControl<string | null>(null, {
      validators: [
        (control) => {
          const address = control.value;

          const currency = this.currency?.() as CurrencyVM | null | undefined;

          if (!address || !currency) {
            return null;
          }

          return currency.validateAddress?.(address) ? null : { invalidAddress: true };
        },
      ],
    }),
  });

  // Observable to Signal Conversions
  currencies = toSignal(this._currencies.currencies$);
  userPaymentMethods = toSignal(this._paymentMethodsViewService.paymentMethods$);
  dailyRedemptionLimit = toSignal(this._accountLimitsView.dailyRedemptionLimits$);
  userState = toSignal(this._userView.state$);
  private _currencyFormValue = toSignal(this.cryptoForm.controls.currency.valueChanges);
  private _tickerFormValue = toSignal(this.cryptoForm.controls.ticker.valueChanges);
  private _addressFormValue = toSignal(this.cryptoForm.controls.address.valueChanges);

  // Computed Signals
  hasPlaythroughRequirement = computed(
    () =>
      !!this.userState()?.playthrough_requirement_id &&
      decimal(this.userState()?.playthrough_progress ?? 0).lt(decimal(this.userState()?.playthrough_requirement ?? 0)),
  );
  fiatRedemptionsEnabledCountry = computed(() => this.userState()?.country_id === 'USA');
  tickers = computed(() =>
    Object.values(
      this.currencies()
        ?.filter((c) => c.enable_redemptions)
        .reduce(
          (acc, c) => {
            acc[c.ticker] = acc[c.ticker] || {
              ticker: c.ticker,
              ui_id: c.ui_id,
              ui_name: c.ui_name,
              ui_label: c.ui_label,
              ui_decimals: c.ui_decimals,
              ui_icon: c.ui_icon,
              display_name: c.display_name,
              currencies: [],
              is_multi_network: c.is_multi_network,
            };

            acc[c.ticker].currencies.push(c);

            return acc;
          },
          {} as Record<string, TickerVM>,
        ) ?? [],
    ),
  );
  ticker = computed(() =>
    this._tickerFormValue() ? this.tickers().find((c) => c.ticker === this._tickerFormValue()) : null,
  );
  availableCurrencies = computed(() =>
    this.ticker() ? this.ticker()!.currencies.filter((n) => n.enable_redemptions) : [],
  );
  currency = computed(() =>
    this.ticker() && (this.availableCurrencies().length > 1 || this.ticker()!.is_multi_network)
      ? this._currencyFormValue()
        ? this.availableCurrencies().find((n: CurrencyVM) => n.currency_id === this._currencyFormValue())
        : null
      : this.ticker()?.currencies[0],
  );
  paymentMethod = computed(
    () =>
      this._addressFormValue() &&
      this.currency() &&
      this.userPaymentMethods() &&
      (this.userPaymentMethods()
        ?.filter(
          (p) =>
            p.payment_method_entity_id === this._addressFormValue() &&
            p.currency_id === this.currency()?.currency_id &&
            p.payment_method_entity_type_id === PaymentMethodEntityTypeId.FireblocksExternalWalletAsset &&
            !p.deleted,
        )
        .sort((a, b) => new PreciseDate(b.created_at).getTime() - new PreciseDate(a.created_at).getTime())?.[0] ??
        null),
  );
  showPaypalWarning = computed(() => this.currency()?.currency_id === CurrencyId.USDC_SOL);
  netAmount = computed(() => {
    const fee = this.feeEstimate();

    return fee === null ? null : decimal(this.amount()).sub(fee).toString();
  });
  // The player has to see the fee and net payout before committing, so the
  // redemption stays gated until the quote resolves.
  awaitingFeeEstimate = computed(() => !!this.currency() && this.feeEstimate() === null);
  dailyLimitExceeded = computed(() => {
    const dailyLimit = this.dailyRedemptionLimit();

    if (!dailyLimit) {
      return false;
    }

    const existingAmount = decimal(dailyLimit.daily_number);
    const limit = decimal(dailyLimit?.daily_redemptions_number_limit ?? 0);

    return existingAmount.add(1).gt(limit);
  });
  networkIconMessageBackgroundColor = computed(() =>
    this.networkIconMajorityColor() ? `rgba(${this.networkIconMajorityColor()}, 0.35)` : null,
  );
  networkIconMessageBorder = computed(() =>
    this.networkIconMajorityColor() ? `1px dashed rgb(${this.networkIconMajorityColor()})` : null,
  );
  nextAvailableDate = computed(() => formatDate(new Date(new Date().setUTCHours(24, 0, 0, 0)), 'short', 'en-US'));

  // Reactive Signals
  redemptionCreateInProgress = signal<boolean>(false);
  feeEstimate = signal<string | null>(null);
  feeEstimateFailed = signal<boolean>(false);
  private readonly _feeEstimateRetry = signal<number>(0);
  submitting = signal<boolean>(false);
  submittingTimedOut = signal<boolean>(false);
  currencyId = signal<string | null>(null);
  networkIconMajorityColor = signal<string | null>(null);

  // Constants
  minimumRedemptionAmount = MINIMUM_REDEMPTION_AMOUNT;
  idempotencyKey = generateId();

  // Trigger that opens once the (approved) payment method is ready and the
  // user has pressed submit. Emitting `null` keeps the gate closed.
  private readonly _submitTrigger = computed(() => {
    const paymentMethod = this.paymentMethod();

    return paymentMethod && paymentMethod.approved_by_payment_provider && this.submitting() ? paymentMethod : null;
  });

  constructor() {
    // Reset dependent fields when the ticker/currency selection changes.
    this.cryptoForm.controls.ticker.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.cryptoForm.controls.address.setValue(null);
      this.cryptoForm.controls.currency.setValue(null);
    });

    this.cryptoForm.controls.currency.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.cryptoForm.controls.address.setValue(null);
    });

    // Network-icon majority color. switchMap cancels a stale fetch when the
    // currency changes mid-request. When the currency has no network icon we
    // emit nothing, leaving the previously resolved color untouched (as before).
    toObservable(this.currency)
      .pipe(
        switchMap((currency) => {
          if (!currency?.network_ui_icon) {
            return EMPTY;
          }

          const iconUrl = `${environment.cdnUrl}/icons/${currency.network_ui_icon.replace(':', '/')}.svg`;

          return this._http.get(iconUrl, { responseType: 'blob' }).pipe(
            switchMap((blob) => from(getMajorityColorFromBlob(blob))),
            catchError((err) => {
              console.error(err);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((color) => this.networkIconMajorityColor.set(color));

    // Fireblocks fee estimate. switchMap keeps only the latest currency's fee.
    // The quote is shown before submit, so it cannot wait on a payment method —
    // a freshly entered address only has one part-way through the submit.
    combineLatest([toObservable(this.currency), toObservable(this._feeEstimateRetry)])
      .pipe(
        tap(() => {
          this.feeEstimate.set(null);
          this.feeEstimateFailed.set(false);
        }),
        switchMap(([currency]) =>
          currency
            ? from(
                this._redemptionsApi.estimateFireblocksRedemptionFee(currency.currency_id, this.amount().toString()),
              ).pipe(
                catchError((err) => {
                  this._logger.error('Failed to estimate the Fireblocks redemption fee', err);
                  this.feeEstimateFailed.set(true);
                  return EMPTY;
                }),
              )
            : EMPTY,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((estimate) => this.feeEstimate.set(estimate.feeAmountInTransactionCurrency));

    // Create the redemption once submitted. exhaustMap ignores further
    // emissions while a create is in flight (replaces redemptionCreateInProgress).
    toObservable(this._submitTrigger)
      .pipe(
        filter((paymentMethod): paymentMethod is NonNullable<typeof paymentMethod> => !!paymentMethod),
        exhaustMap((paymentMethod) => {
          this.redemptionCreateInProgress.set(true);

          return from(
            this._redemptionsApi.createRedemption(
              this.idempotencyKey,
              paymentMethod.payment_method_id,
              this.amount().toString(),
            ),
          ).pipe(
            tap((redemption) => {
              this._analytics.trackEvent(AnalyticsEvent.RedemptionInitiated, {
                $insert_id: redemption.redemption_id,
                value: this.amount(),
                currency: 'USD',
                provider: 'fireblocks',
                transaction_id: redemption.redemption_id,
              });

              this._activeRedemptionViewService.setActiveRedemption(redemption);
            }),
            catchError((err: any) => {
              const e = err as HttpErrorResponse;
              this.idempotencyKey = generateId();
              this._logger.error('Failed to create redemption', { error: err });

              if (isDevMode()) {
                console.error(err);
              }

              let errorMessage = e.error?.message ?? e.message;
              let errorTitle = 'Redemption Failed';

              if (e.error?.code) {
                const errHandle = this._redemptionsApi.ErrorMap?.[e?.error?.code];

                if (errHandle) {
                  errorMessage = errHandle.message;
                  errorTitle = errHandle.title;
                }
              }

              this._alertService.notifyDanger(errorTitle, errorMessage);
              return EMPTY;
            }),
            finalize(() => {
              this.submitting.set(false);
              this.redemptionCreateInProgress.set(false);
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    // Submitting timeout/fallback: without this the screen could hang forever
    // waiting on Fireblocks approval if the webhook never lands. When
    // `submitting` flips true we start a 30s timer (matches the native
    // _cryptoApprovalTimeout); if approval arrives first, `submitting` flips
    // false and switchMap cancels the timer via EMPTY. Declarative per the
    // no-effect rule -- RxJS `timer`, not setTimeout.
    toObservable(this.submitting)
      .pipe(
        switchMap((submitting) => (submitting ? timer(SUBMITTING_TIMEOUT_MS) : EMPTY)),
        tap(() => {
          this.idempotencyKey = generateId();
          this.submitting.set(false);
          this.submittingTimedOut.set(true);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  retryFeeEstimate(): void {
    this._feeEstimateRetry.update((n) => n + 1);
  }

  async submit() {
    this.cryptoForm.markAllAsTouched();
    const currency = this.currency();

    if (!this.cryptoForm.valid || !currency || this.feeEstimate() === null) {
      return;
    }

    this.submittingTimedOut.set(false);
    this.submitting.set(true);

    if (!this.paymentMethod()) {
      const { address } = this.cryptoForm.value;

      try {
        await this._paymentMethods.createFireblocksRedemptionAddress(currency.currency_id, address!);

        await this._paymentMethodsViewService.syncPaymentMethods();

        // Re-reconcile on this first attempt rather than depending solely on the
        // Fireblocks webhook: the sync creates the PM once the address is APPROVED
        // and pushes it via realtime, which the approval effect picks up.
        // Non-critical -- the screen-open sync and timeout fallback cover the rest.
        this._paymentMethods.syncFireblocksRedemptionPaymentMethods().catch(() => {
          /* swallow */
        });
      } catch (err: any) {
        this.idempotencyKey = generateId();
        this._logger.error('Failed to create redemption address', {
          error: err,
        });

        const errorDesc = err?.error?.code
          ? (this._paymentMethods.ErrorMap as Record<string, any>)[err.error.code]
          : null;

        this._alertService.notify(
          errorDesc?.title ?? 'Address Creation Failed',
          errorDesc?.message ?? 'An error occurred while adding your redemption address. Please try again.',
          errorDesc?.type ?? AlertType.Danger,
        );

        this.submitting.set(false);
      }
    }
  }
}
